import { useEffect, useState } from 'react'
import * as actions from '@actions'
import * as store from '@store'
import CustomModal, { ModalBody, ModalFooter, ModalHeader } from '@/components/shared/CustomModal'
import { useWireValue } from '@forminator/react-wire'
import { toast } from 'react-toastify'
import { deedFitInTab, leaseFitInTab, separateDocumentsByTab } from '../Runsheet/Runsheet.lib'
import SuggestionTabs from './SuggestionTabs'
import DocumentCategories from './DocumentCategories'
import UncategorizedData from './UncategorizedData'
import { sortByMultiple } from '@/utils'
import DocumentSelectionDialog from './DocumentSelectionDialog'
import CircleSpinner from '@/components/shared/CircleSpinner'
import { useWireState } from '@forminator/react-wire'
import { Link } from 'react-router-dom'
import { DEDUPE_STATUSES, PURCHASE_TYPE_AUTO_CHAIN } from '@/constants'
import cn from 'clsx'
import { usePollRunsheetDeduping } from '@/hook/usePollRunsheetDeduping'
import usePayments from '@/hook/usePayments'

const DocumentCategorizationModal = ({ showModal, setShowModal }) => {
    
    // const [, setSearchParams] = useSearchParams()
    const [isSaving, setIsSaving] = useState(false)
    const [documents, setDocuments] = useState([])
    const [suggestionTabs, setSuggestionTabs] = useState([])
    const [uncategorized, setUncategorized] = useState([])
    const [partiesToUniqueMap, setPartiesToUniqueMap] = useState({})
    const [tabIdx, setTabIdx] = useState(null)
    const [failedToLoad, setFailedToLoad] = useState(false)
    const [loading, setLoading] = useState(false)
    const [autoChain, setAutoChain] = useState(false)
    
    const tabs = useWireValue(store.runsheetTabs)
    const [runsheet, setRunsheet] = useWireState(store.currentRunsheet)
    
    const payments = usePayments()
    
    const hideMainFunctionalities = !runsheet.categorized && !failedToLoad
        && runsheet.dedupeStatus !== DEDUPE_STATUSES.DEDUPING
    
    const handleClose = () => {
        setShowModal(false)
    }
    
    const sortByDates = documents => sortByMultiple(
        documents,
        ['value.instrumentDate', 'value.fileDate', 'value.recordingDate'],
        'date',
    )
    
    const addToUncategorized = documents => {
        const docsToAdd = documents.filter(d => !uncategorized.some(u => u.id === d.id))
        const newUncategorized = sortByDates([...uncategorized, ...docsToAdd])
        
        setUncategorized(newUncategorized)
    }
    
    const handleBulkChain = async fundingSource => {
        const chainerCount = await actions.bulkChainRunsheet(runsheet.id, fundingSource)
        
        toast.success(`Successfully started ${chainerCount.length} Deed and Leasehold Chains.`)
    }
    
    const handleCategorize = async () => {
        setIsSaving(true)
        
        try {
            
            let lastOrder = tabs[tabs.length - 1]?.tabOrder || 1
            const promises = suggestionTabs.map(async st => {
                const tab = await actions.createRunsheetTab(runsheet.id,
                    { name: st.name, tabOrder: ++lastOrder, type: st.type })
                
                return actions.addDocumentValuesToRunsheetTab(
                    runsheet.id, tab.id, st.documents.map(d => d.document.id))
            })
            
            await Promise.all(promises)
            
            await actions.updateRunsheet(runsheet.id, {
                categorized: true,
            })
            
            setRunsheet(prev => ({
                ...prev,
                categorized: true,
            }))
            
            toast.success('Successfully created Suggested Tabs.')
            
            handleClose()
            
            if (autoChain) {
                
                try {
                    
                    if (!payments.showPurchaseDialog({
                        purchaseType: PURCHASE_TYPE_AUTO_CHAIN,
                        itemCount: suggestionTabs.length,
                        bulkItemCount: suggestionTabs.map(st => st.documents.length),
                        onConfirmPayment:
                            (remainingBalance, fundingSource) => handleBulkChain(fundingSource),
                    })) {
                        await handleBulkChain()
                    }
                    
                } catch (err) {
                    console.log(err)
                    toast.error('Failed to Chain Tabs. Please try again on each Tab individually.')
                }
            }
            
        } catch (err) {
            console.log(err)
            toast.error('Failed to create Suggested Tabs.')
        }
        
        setIsSaving(false)
    }
    
    const toggleCategory = (key, idx, value) => {
        
        const document = documents[idx]
        const newDocument = { ...document, category: { ...document.category, [key]: value } }
        
        documents[idx] = newDocument
        setDocuments([...documents])
        
        // if im making a document a lease or a deed, i automatically try to assign it to a tab
        if (value) {
            
            const suggestionTabsCopy = [...suggestionTabs]
            
            if (key === 'lease') {
                leaseFitInTab(suggestionTabsCopy, documents[idx], partiesToUniqueMap, true)
            } else {
                deedFitInTab(suggestionTabsCopy, documents[idx])
            }
            
            setSuggestionTabs(suggestionTabsCopy)
            
            if (uncategorized.some(uc => uc.id === document.id)) {
                setUncategorized(uncategorized.filter(uc => uc.id !== document.id))
            }
            // if i uncheck both deed and leases from a document, it gets back to uncategorized,
            // even if its a part of a tab
        } else if (!newDocument.category.deed && !newDocument.category.lease) {
            addToUncategorized([newDocument])
        }
        
    }
    
    const removeTab = tabIdx => {
        const tab = suggestionTabs[tabIdx]
        const newTabs = suggestionTabs.filter((_, idx) => idx !== tabIdx)
        
        setSuggestionTabs(newTabs)
        
        const documentIdsSet = new Set()
        
        tab.documents.forEach(d => documentIdsSet.add(d.id))
        
        setUncategorized(uncategorized.filter(uc => !documentIdsSet.has(uc.id)))
        
        const docsToAdd = tab.documents.filter(document =>
            !newTabs.some(tab => tab.documents.some(d => d.id === document.id)),
        )
        
        if (docsToAdd.length)
            addToUncategorized(docsToAdd)
        
    }
    
    const removeDocumentFromTab = (tabIdx, documentId) => {
        
        const oldTab = suggestionTabs[tabIdx]
        const tab = { ...oldTab, documents: oldTab.documents.filter(doc => doc.id !== documentId) }
        
        suggestionTabs[tabIdx] = tab
        
        setSuggestionTabs([...suggestionTabs])
        
        if (!suggestionTabs.some(tab => tab.documents.some(d => d.id === documentId))) {
            const document = documents.find(d => d.id === documentId)
            
            if (document)
                addToUncategorized([document])
        }
        
    }
    
    const addDocumentsToTab = (tabIdx, documents) => {
        
        const oldTab = suggestionTabs[tabIdx]
        const tab = { ...oldTab, documents: sortByDates([...oldTab.documents, ...documents]) }
        
        suggestionTabs[tabIdx] = tab
        
        setSuggestionTabs([...suggestionTabs])
        
        const documentIdsSet = new Set()
        
        documents.forEach(d => documentIdsSet.add(d.id))
        setUncategorized(uncategorized.filter(uc => !documentIdsSet.has(uc.id)))
    }
    
    const mapUniqueParties = uniqueParties => {
        const partiesMap = {}
        
        uniqueParties.forEach(up => {
            
            up.parties.forEach(p => {
                if (!partiesMap[p.id])
                    partiesMap[p.id] = up.id
            })
        })
        
        setPartiesToUniqueMap(partiesMap)
        
        return partiesMap
    }
    
    const loadDocuments = async () => {
        
        try {
            
            setFailedToLoad(false)
            setLoading(true)
            
            const rs = await actions.dedupeParties(runsheet.id)
            
            if (rs.dedupeStatus === DEDUPE_STATUSES.DEDUPED) {
                
                const uniqueParties = await actions.listUniqueParties(runsheet.id)
                const partiesMap = mapUniqueParties(uniqueParties)
                const data = separateDocumentsByTab(runsheet, partiesMap)
                
                setDocuments(data.documents)
                setSuggestionTabs(data.tabs)
                setUncategorized(data.uncategorized)
                
            }
            
        } catch (err) {
            console.error(err)
            setFailedToLoad(true)
            toast.error('Failed to load documents for Sorting.')
            
        } finally {
            setLoading(false)
        }
        
    }
    
    usePollRunsheetDeduping(runsheet.dedupeStatus === DEDUPE_STATUSES.DEDUPING ? runsheet.id : null)
    
    useEffect(() => {
        
        if (!runsheet) return
        
        if (runsheet.categorized || runsheet.dedupeStatus === DEDUPE_STATUSES.DEDUPING) return
        
        loadDocuments()
        
    }, [runsheet])
    
    return (
        <CustomModal open={showModal} modalHandler={handleClose}
            className={cn('max-h-screen', { 'min-w-[90vw]': hideMainFunctionalities })}>
            
            <ModalHeader>
                Sort Documents
                <div className="text-sm text-slate-400">
                    Documents will be linked to a new tab based on Instrument Type, Parties and Dates
                </div>
            </ModalHeader>
            
            <ModalBody>
                
                {loading &&
                    <div className="flex items-center justify-center">
                        <CircleSpinner className="flex items-center" />
                    </div>}
                
                {!loading && runsheet.categorized && <p className="text-lg mt-5">
                    The documents have already been sorted to specific tabs.
                </p>}
                
                {failedToLoad && <div className="flex flex-col items-center">
                    <div className="text-lg mt-2">
                        We're sorry for the inconvenience, but documents failed to load for sorting.
                    </div>
                    <div className="text-slate-400 -mt-1 mb-5">
                        Please retry and if the problem persists let us know and we'll be happy to help.
                    </div>
                    <button className="btn btn-primary" onClick={loadDocuments}>Retry</button>
                </div>}
                
                {!loading && !failedToLoad && runsheet.dedupeStatus === DEDUPE_STATUSES.DEDUPING &&
                    <div className="flex flex-col items-center">
                        <div className="text-lg mt-2 mb-5">
                            Please wait... Your documents are being sorted.
                        </div>
                        <div className="flex items-center justify-center">
                            <CircleSpinner className="flex items-center" />
                        </div>
                    </div>}
                
                {hideMainFunctionalities &&
                    <>
                        <DocumentCategories documents={documents} toggleCategory={toggleCategory} />
                        
                        <SuggestionTabs
                            tabs={suggestionTabs}
                            setTabs={setSuggestionTabs}
                            setTabIdx={setTabIdx}
                            removeDocumentFromTab={removeDocumentFromTab}
                            removeTab={removeTab} />
                        
                        {uncategorized.length > 0 && <UncategorizedData uncategorized={uncategorized} />}
                        
                        {tabIdx !== null &&
                            <DocumentSelectionDialog
                                documents={documents}
                                suggestionTabs={suggestionTabs}
                                tabIdx={tabIdx}
                                setTabIdx={setTabIdx}
                                addDocumentsToTab={addDocumentsToTab} />}
                    </>
                }
            
            </ModalBody>
            
            <ModalFooter>
                {hideMainFunctionalities && <div className="flex items-center gap-2 justify-between flex-1">
                    <span
                        className="tooltip tooltip-right"
                        data-tip="Discard categorization and Chain all Documents">
                        <Link
                            className="btn btn-secondary"
                            disabled={isSaving}
                            to={`/runsheet/${runsheet.id}/title-breakdown?activeTab=0`}>
                            Chain Main Tab only
                        </Link>
                    </span>
                    
                    
                    <div className="flex items-center gap-2">
                        
                        <label className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                className="checkbox checkbox-sm"
                                onClick={evt => setAutoChain(evt.target.checked)} />
                            Auto-chain Tabs after creation
                        </label>
                        
                        <button
                            className="btn btn-primary btn-outline"
                            disabled={isSaving}
                            onClick={handleClose}>
                        Cancel
                        </button>
                        
                        <button
                            disabled={isSaving || !suggestionTabs.length}
                            className="btn btn-primary"
                            onClick={handleCategorize}>
                            {isSaving && <><CircleSpinner className="flex itFems-center" />Creating tabs...</>}
                            {!isSaving && 'Create Tabs'}
                        </button>
                    </div>
                </div>}
            </ModalFooter>
        
        </CustomModal>
    )
}

export default DocumentCategorizationModal